// The light at the observer's eye, from the atmosphere's tables (atmospherePhysics.ts) and where the star and the moon are seen from the observer (bodies.ts sunSeen, moonSeen). No three.js.
//
// This replaces the sun message's light (direct_rgb, sky_rgb), which the server works out for the world's own point and which the client had used wherever it stood. Here the
// beam and the sky's light are those of the eye's height, with the star's elevation at the eye: walk to where it is dusk, or climb out of the air, and they follow, with nothing else to
// tell the renderer where it is. The numbers are in the sun message's units: the beam normal to itself (`direct`, `clear`), and the diffuse light on an open horizontal surface (`sky`).
//
// The pieces of the cloud filter (skyLight.ts: sky = open * mh * dome + clear * mu * (sc * dome + tbh * (1 - e) * ftr) + floor) are those of a physical atmosphere: the open sky is the tables'
// diffuse irradiance, the beam is not otherwise dimmed by the high layer (tbh 1), and nothing is added from the clear sky's share of the beam (sc 0: the tables already hold it in `open`).
// `floor` is what the night leaves when no star is up: the airglow (the atmosphere message's), and the moon's light on a horizontal surface, which the cloud does not change in the filter and
// which the renderer corrects for the cloud it draws (DefaultSimRenderer, the moon's beam through the volume). Starlight is not in the tables: it comes from the stars message's table by spin angle (starlight.ts) and is added to `open`, so the cloud filter dims it like the open sky's own light (the stars are behind the cloud).
import type { Atmosphere, V3 } from './atmospherePhysics';

export interface EyeLight {
  /** The star's elevation at the eye (rad), geometric. */
  elevation: number;
  /** The beam normal to itself, through the air, with the disc's lower limb through the planet's horizon softened (0 with the star's disc wholly below it). */
  direct: V3;
  /** The same beam: what the cloud is lit with before the cloud (the filter's `clear`). */
  clear: V3;
  /** The diffuse light on an open horizontal surface (the sky's and the stars', no cloud) plus the floor. */
  sky: V3;
  /** The moon's beam normal to itself, through the air and no cloud, or null with no moon. */
  moon: V3 | null;
  /** The pieces of the cloud filter for this eye. */
  atm: { open: number[]; floor: number[]; mh: number; sc: number; tbh: number };
}

export interface MoonIn {
  /** Unit direction to the moon in the render axes (y up at the eye). */
  dir: ArrayLike<number>;
  angR: number;
  /** The moon's beam outside the air, normal to itself, in the sun message's units (the registry's disc mean times the solid angle). */
  toa: ArrayLike<number>;
}

/**
 * The mean transmittance of the beam over a disc of angular radius `angR` whose centre is at cosine `mus` from the zenith: three taps across its radius (the planet's horizon cuts the
 * disc, and the beam changes by orders of magnitude over the last degree). `out` takes a colour.
 */
export function discTrans(A: Atmosphere, h: number, mus: number, angR: number, out: number[]): number[] {
  const z = Math.acos(Math.min(1, Math.max(-1, mus))), b = [0, 0, 0] as number[];
  let tr = 0, tg = 0, tb = 0;
  for (let k = -1; k <= 1; k++) {
    A.trans3(h, Math.cos(z + k * angR * 0.7), b);
    const w = k === 0 ? 2 : 1;
    tr += w * b[0]!; tg += w * b[1]!; tb += w * b[2]!;
  }
  out[0] = tr * 0.25; out[1] = tg * 0.25; out[2] = tb * 0.25;
  return out;
}

const T3 = [0, 0, 0] as number[];

/**
 * @param A     the atmosphere
 * @param h     the eye's height above the planet (m)
 * @param dir   the unit direction to the star in the render axes (y up at the eye)
 * @param angR  the star's angular radius (rad)
 * @param flux  the star's flux outside the atmosphere, in the world's units
 * @param moon  the moon as the observer sees it, or none
 * @param starlight  the floor the stars give a horizontal surface here (starlight.ts), or none
 */
export function eyeLight(A: Atmosphere, h: number, dir: ArrayLike<number>, angR: number, flux: number, moon?: MoonIn | null, starlight?: ArrayLike<number> | null): EyeLight {
  const mus = Math.min(1, Math.max(-1, dir[1]!));
  const el = Math.asin(mus);
  discTrans(A, h, mus, angR, T3);
  const f = flux;
  const direct: V3 = [f * A.sunToa[0] * T3[0]!, f * A.sunToa[1] * T3[1]!, f * A.sunToa[2] * T3[2]!];
  const e = A.esky3(h, mus) as number[];
  const open = [flux * e[0]!, flux * e[1]!, flux * e[2]!];
  if (starlight) for (let c = 0; c < 3; c++) open[c]! += starlight[c]!;
  // the moon: its beam through the air along its own direction, and its light on an open horizontal surface (in the floor, as the server's was)
  let mb: V3 | null = null;
  const fl = [A.airglow, A.airglow, A.airglow];
  if (moon) {
    const mm = Math.min(1, Math.max(-1, moon.dir[1]!));
    discTrans(A, h, mm, moon.angR, T3);
    mb = [moon.toa[0]! * T3[0]!, moon.toa[1]! * T3[1]!, moon.toa[2]! * T3[2]!];
    if (mm > 0) for (let c = 0; c < 3; c++) fl[c]! += mb[c]! * mm;
  }
  return {
    elevation: el, direct, clear: [direct[0], direct[1], direct[2]],
    sky: [open[0]! + fl[0]!, open[1]! + fl[1]!, open[2]! + fl[2]!],
    moon: mb,
    atm: { open, floor: fl, mh: 1, sc: 0, tbh: 1 },
  };
}
